import WarningIcon from '@mui/icons-material/Warning'
import IconButton from '@mui/material/IconButton'
import { useQuery } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverMap from 'components/KNDetailsPopover/KNDetailsPopoverMap'
import KNDetailsPopoverMapLoader from 'components/KNDetailsPopover/KNDetailsPopoverMapLoader'
import KNDetailsPopoverSection from 'components/KNDetailsPopover/KNDetailsPopoverSection'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { KNPositionTrackingIcon, KNPositionWarningIcon } from 'components/KNIcon/KNMaterialIcon'
import type { MapMarker } from 'components/KNMapLibre/types'
import { isFuture, parseISO } from 'date-fns'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import React, { type ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { TrackingHealth, TrackingStatus, type TripData } from 'screens/TripDashboard/TripDashboard.types'
import { groupGeoPointsBySpeed, positionDataTransformer } from './TripDetails.helpers'
import { tripLegsQueryOptions, tripVehiclePositionsQueryOptions } from './TripDetails.service'
import type { LegData } from './TripDetails.types'

export interface TrackingProps {
  trip: TripData
  weblinkToken?: string
}

const getBaseMarkers = (legs: LegData[]): MapMarker[] =>
  legs.reduce((markers: MapMarker[], leg) => {
    leg.wayPoints.forEach((stop) => {
      if (stop.geoPoint) {
        markers.push({
          id: stop.wayPointCid,
          latitude: stop.geoPoint.latitude,
          longitude: stop.geoPoint.longitude,
          type: stop.type,
        })
      }
    })
    return markers
  }, [])

const Tracking = ({ trip, weblinkToken }: TrackingProps): ReactElement | null => {
  const { t } = useTranslation()
  const location = useLocation()

  const isTracked = Boolean(
    trip.trackingInformation
    && [TrackingStatus.IN_PROGRESS, TrackingStatus.STOPPED].includes(trip.trackingInformation.trackingStatus)
    && trip.trackingInformation.positionTrackingStatus !== TrackingHealth.NONE
  )
  const hasTrackingError = trip.trackingInformation?.positionTrackingStatus === TrackingHealth.UNHEALTHY

  const trackingDetailsPopover = usePopoverState()
  const legsQuery = useQuery({
    ...tripLegsQueryOptions(trip.entityId, weblinkToken),
    enabled: trackingDetailsPopover.open,
  })
  const positionsQuery = useQuery({
    ...tripVehiclePositionsQueryOptions(trip.entityId, weblinkToken),
    enabled: trackingDetailsPopover.open && isTracked,
  })
  const geoPoints = useMemo(() => {
    if (!positionsQuery.isSuccess) {
      return []
    }
    return positionDataTransformer(positionsQuery.data)
  }, [positionsQuery])
  const groupedGeoPoints = useMemo(() => groupGeoPointsBySpeed(geoPoints), [geoPoints])
  const markers = useMemo(() => {
    if (!legsQuery.isSuccess) {
      return []
    }
    const baseMarkers = getBaseMarkers(legsQuery.data)
    const lastVehiclePosition = geoPoints[geoPoints.length - 1]
    if (!lastVehiclePosition?.longitude || !lastVehiclePosition.latitude) {
      return baseMarkers
    }
    return [{
      id: 'vehicle',
      latitude: lastVehiclePosition.latitude,
      longitude: lastVehiclePosition.longitude,
      type: 'VEHICLE',
    }, ...baseMarkers]
  }, [legsQuery, geoPoints])

  const handleTrackingDetailsClickWrapper = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      trackingDetailsPopover.handleClick(event)
      analyticsEvent('polestar_cs_leg_tracking_popover', [getRouteName(location.pathname)])
    },
    [location],
  )

  if (!isTracked || (trip.earlyPickUpDate && isFuture(parseISO(trip.earlyPickUpDate)))) {
    return null
  }

  return (
    <>
      <IconButton
        onClick={handleTrackingDetailsClickWrapper}
        size="small"
        sx={{
          padding: 0,
          color: hasTrackingError ? 'warning.main' : 'primary.main',
        }}
      >
        {hasTrackingError ? (
          <KNPositionWarningIcon />
        ) : (
          <KNPositionTrackingIcon />
        )}
      </IconButton>

      <KNDetailsPopover
        open={trackingDetailsPopover.open}
        onClose={trackingDetailsPopover.handleClose}
        anchorEl={trackingDetailsPopover.anchorEl}
        rightAligned={true}
        sx={{
          maxHeight: 'auto',
        }}
      >
        <KNDetailsPopoverSection
          header={trip.tripNumber}
          items={hasTrackingError ? [
            (
              <KNCaption
                key="warning"
                iconColor="inherit"
                fontColor="inherit"
                icon={<WarningIcon />}
                sx={{
                  paddingX: 2,
                  paddingY: 0.25,
                }}
              >
                {t('screens.cs.trip_dashboard.card.tracking_warning')}
              </KNCaption>
            ),
          ] : []}
        />
        {legsQuery.isPending && (
          <KNDetailsPopoverMapLoader />
        )}
        {legsQuery.isSuccess && (
          <KNDetailsPopoverMap markers={markers} geoPoints={geoPoints} groupedGeoPoints={groupedGeoPoints} />
        )}
      </KNDetailsPopover>
    </>
  )
}

export default Tracking
